import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface ReadingProgressProps {
  className?: string;
  color?: string;
  height?: number;
}

export const ReadingProgress = ({
  className,
  height = 3,
}: ReadingProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      if (docHeight <= 0) {
        setProgress(0);
        setIsVisible(false);
        return;
      }
      
      const scrollProgress = Math.min(100, Math.max(0, (scrollTop / docHeight) * 100));
      setProgress(scrollProgress);
      
      // Show after scrolling past 50px
      setIsVisible(scrollTop > 50);
    };

    // Initial calculation
    updateProgress();

    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  return (
    <div
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-opacity duration-300",
        isVisible ? "opacity-100" : "opacity-0",
        className
      )}
      style={{ height }}
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Reading progress"
    >
      {/* Background track */}
      <div className="absolute inset-0 bg-muted/50" />
      
      {/* Progress fill */}
      <div
        className="h-full bg-primary transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export default ReadingProgress;


